import React, {
  useCallback,
  useEffect,
  useReducer
} from 'react';
import { useIntl } from 'react-intl';
import { connect } from 'react-redux';

import {
  CircularProgress,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@material-ui/core';
import { Clear as IconClear } from '@material-ui/icons';

import {
  AutocompleteOption,
  ChecklistAutocomplete
} from '@checkit/react-components';

import { loaderReducer } from '../../common/delayedLoader';
import { EntitySnapshotWithType } from '../state';

import {
  RenderEntityAutocompleteContext,
  RenderEntityTableContext,
  SelectedEntityPanel
} from './SelectedEntityPanel';
import { messages } from './translations';

// TODO: move me to schedules/state.ts
type EntitySnapshotForSchedule = EntitySnapshotWithType<'schedule'>;

/** A component representing a single schedule shown in the schedules list.  */
const SelectedSchedulesTableItem = connect(
  (/* { checks }: State */) => ({ /* checks */ }) // TODO: get schedules from state
)(function SelectedSchedulesTableItemImpl({ snapshot, isDeletable, handleDelete }: {
  /** The schedule entity snapshot represented by this table item. */
  snapshot: EntitySnapshotForSchedule;
  /** Whether the user is allowed to remove this schedule from the current library. */
  isDeletable: boolean;
  /** Removes the schedule corresponding to the given snapshot from the library's selected schedules. */
  handleDelete: (snapshot: EntitySnapshotForSchedule) => void;
}) {
  const { formatMessage } = useIntl( );

  // TODO: fetch the schedule's data by code once passed in

  const [ loadingState, loadingDispatch ] = useReducer(loaderReducer, {
    displayLoader: true,
    loading: false
  });

  useEffect(function simulateLoading( ) {
    loadingDispatch('loading');
    const timer = setTimeout(function simulateLoaded( ) {
      loadingDispatch('loaded');
    }, 1000);
    return ( ) => clearTimeout(timer);
  }, [ ]);

  /** Removes this schedule from the library. */
  function onClickDelete( ) {
    handleDelete(snapshot);
  }

  return loadingState.displayLoader ? (
    <TableRow>
      <TableCell align="center" colSpan={4}>
        <CircularProgress color="inherit" size={12} />
      </TableCell>
    </TableRow>
  ) : (
    <TableRow>
      <TableCell component="th" scope="row">{snapshot.code}</TableCell>
      <TableCell padding="none">
        <IconButton
          aria-label="delete"
          size="small"
          onClick={onClickDelete}
          title={formatMessage(messages['schedule.table.removeitem'])}
          disabled={!isDeletable}
        >
          <IconClear fontSize="inherit" />
        </IconButton>
      </TableCell>
    </TableRow>
  );
});

/** A table of schedules selected by the user for inclusion in a library. */
function SelectedSchedulesTable({ schedules, isDeletable, handleDelete }: {
  /** An array of schedule names to display in the table. */
  schedules: EntitySnapshotForSchedule[];
  /** A callback which handles the logic to decide whether a given schedule can be removed by the user. */
  isDeletable: (schedule: EntitySnapshotForSchedule) => boolean;
  /** Removes the schedule corresponding to the given snapshot from the library's selected schedules. */
  handleDelete: (snapshot: EntitySnapshotForSchedule) => void;
}) {
  const { formatMessage } = useIntl( );

  const items = schedules.map((snapshot) => (
    <SelectedSchedulesTableItem
      key={snapshot.id}
      snapshot={snapshot}
      isDeletable={isDeletable(snapshot)}
      handleDelete={handleDelete}
    />
  ));

  return (
    <TableContainer component={Paper}>
      <Table size="small" aria-label={formatMessage(messages['schedule.table.label'])}>
        <TableHead>
          <TableRow>
            <TableCell>{formatMessage(messages['schedule.table.header.name'])}</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>{items}</TableBody>
      </Table>
    </TableContainer>
  );
}

/** An expandable form panel for selecting checks for inclusion in a library. */
export function SelectedSchedulesPanel({ selected, setSelected, isLibrarySaved, existing }: {
  /** The schedules which have been selected. */
  selected: EntitySnapshotForSchedule[];
  /** A callback used to signal the fact that the selected schedules have changed. */
  setSelected: (selection: EntitySnapshotForSchedule[]) => void;
  /** Whether the library these schedules are in has actually been saved to the server. */
  isLibrarySaved: boolean;
  /** The schedules which are already present in the server-persisted version of the library. */
  existing: EntitySnapshotForSchedule[];
}) {
  const { formatMessage } = useIntl( );
  const heading = formatMessage(messages['schedule.form.heading']);

  const isDeletable = useCallback(function isScheduleDeletable(schedule) {
    return !existing.some((e) => e.code === schedule.code);
  }, [ existing ]);

  const renderAutocomplete = ({ handleAdd }: RenderEntityAutocompleteContext<'schedule'>) => {
    // TODO: implement logic to call handleAdd()
    const onAutocompleteOptionSelected = (
      _: React.ChangeEvent<{ }>,
      option: AutocompleteOption | null
    ) => null;
    // TODO: replace with a different autocomplete
    return (
      <ChecklistAutocomplete
        label={formatMessage(messages['check.table.header.name'])}
        onChange={onAutocompleteOptionSelected}
        fullWidth={true}
      />
    );
  };
  const renderTable = ({ handleDelete }: RenderEntityTableContext<'schedule'>) => (
    <SelectedSchedulesTable schedules={selected} isDeletable={isDeletable} handleDelete={handleDelete} />
  );

  return (
    <SelectedEntityPanel
      type="schedule"
      heading={heading}

      selected={selected}
      setSelected={setSelected}
      isLibrarySaved={isLibrarySaved}

      Autocomplete={renderAutocomplete}
      Table={renderTable}
    />
  );
}
